import { useMutation, useQuery } from '@tanstack/react-query';
import {
  type Core_SwitchSubjectGroupType,
  EmulateHeaders,
  type QueryAdditionalOptions,
  type SubjectGroupFilter,
  SubjectGroupType,
  type SubjectGroupsQuery,
  type UpdateSubjectGroupInput,
  type UseQueryReturnType,
  gqlClient,
  graphql,
  queryClient,
} from '@tyro/api';
import { naturalSort, sortByDisplayName } from '@tyro/core';
import { useCallback } from 'react';
import { groupsKeys } from './keys';

const subjectGroupsList = graphql(/* GraphQL */ `
  query subjectGroups($filter: SubjectGroupFilter!) {
    subjectGroups(filter: $filter) {
      partyId
      name
      avatarUrl
      subjects {
        id
        name
        colour
        nationalCode
        icon
      }
      studentMembers {
        memberCount
      }
      staff {
        firstName
        lastName
        avatarUrl
      }
      irePP {
        level
        examinable
      }
      programmeStages {
        programme {
          name
        }
      }
      yearGroups {
        name
      }
      studentMembershipType {
        type
        classGroupId
        blockId
        classGroupName
      }
    }
  }
`);

const subjectGroupById = graphql(/* GraphQL */ `
  query subjectGroupById($filter: SubjectGroupFilter!) {
    subjectGroups(filter: $filter) {
      partyId
      name
      avatarUrl
      yearGroups {
        name
      }
      subjects {
        name
        colour
        icon
      }
      studentMembershipType {
        type
        classGroupId
        blockId
        classGroupName
      }
      staff {
        title {
          id
          name
          nameTextId
        }
        firstName
        lastName
        type
      }
      students {
        partyId
        classGroup {
          name
        }
        person {
          partyId
          firstName
          lastName
          avatarUrl
          type
          title {
            id
            name
            nameTextId
          }
        }
        extensions {
          priority
          aen
          medical
        }
      }
    }
  }
`);

const updateSubjectGroups = graphql(/* GraphQL */ `
  mutation core_updateSubjectGroups($input: [UpdateSubjectGroupInput!]) {
    core_updateSubjectGroups(input: $input) {
      success
    }
  }
`);

const switchSubjectGroupType = graphql(/* GraphQL */ `
  mutation core_switchSubjectGroupType($input: Core_SwitchSubjectGroupType!) {
    core_switchSubjectGroupType(input: $input) {
      success
    }
  }
`);

const subjectGroupsQuery = {
  list: (academicNamespaceId?: number) => ({
    queryKey: groupsKeys.subject.groups(academicNamespaceId),
    queryFn: async () =>
      gqlClient.request(
        subjectGroupsList,
        {
          filter: {
            partyIds: [] as number[],
            type: [SubjectGroupType.SubjectGroup],
            groupTagIds: [],
          },
        },
        academicNamespaceId
          ? {
              [EmulateHeaders.ACADEMIC_NAMESPACE_ID]:
                academicNamespaceId.toString(),
            }
          : {},
      ),
  }),
  details: (id?: number) => ({
    queryKey: groupsKeys.subject.details(id),
    queryFn: () =>
      gqlClient.request(subjectGroupById, {
        filter: { partyIds: [id ?? 0], groupTagIds: [] },
      }),
  }),
  detailsByFilter: (filter: SubjectGroupFilter) => ({
    queryKey: groupsKeys.subject.detailsByFilter(filter),
    queryFn: () =>
      gqlClient.request(subjectGroupById, {
        filter,
      }),
  }),
};

export function getSubjectGroups(
  academicNamespaceId?: QueryAdditionalOptions['academicNamespaceId'],
) {
  return queryClient.fetchQuery(subjectGroupsQuery.list(academicNamespaceId));
}

export function getSubjectGroupById(id?: number) {
  return queryClient.fetchQuery(subjectGroupsQuery.details(id));
}

export function getSubjectGroupByFilter(filter: SubjectGroupFilter) {
  return queryClient.fetchQuery(subjectGroupsQuery.detailsByFilter(filter));
}

export function useSubjectGroups(
  {
    enabled = true,
    academicNamespaceId,
  }: QueryAdditionalOptions | undefined = {
    enabled: true,
    academicNamespaceId: undefined,
  },
) {
  return useQuery({
    ...subjectGroupsQuery.list(academicNamespaceId),
    enabled,
    select: useCallback(
      ({ subjectGroups }: SubjectGroupsQuery) =>
        subjectGroups?.sort((a, b) => naturalSort(a.name, b.name)),
      [enabled, academicNamespaceId],
    ),
  });
}

export function useSubjectGroupById(id?: number) {
  return useQuery({
    ...subjectGroupsQuery.details(id),
    select: ({ subjectGroups }) => {
      if (!subjectGroups) return null;

      const [group] = subjectGroups || [];

      return {
        ...group,
        staff: group.staff?.sort(sortByDisplayName),
        students: group.students?.sort((studentA, studentB) =>
          sortByDisplayName(studentA.person, studentB.person),
        ),
      };
    },
  });
}

export function useSubjectGroupByFilter(filter: SubjectGroupFilter) {
  return useQuery({
    ...subjectGroupsQuery.detailsByFilter(filter),
    select: ({ subjectGroups }) => {
      if (!subjectGroups) return null;

      const [group] = subjectGroups || [];

      return {
        ...group,
        staff: group.staff?.sort(sortByDisplayName),
        students: group.students?.sort((studentA, studentB) =>
          sortByDisplayName(studentA.person, studentB.person),
        ),
      };
    },
  });
}

export function useSaveSubjectGroupEdits() {
  return useMutation({
    mutationFn: (input: UpdateSubjectGroupInput[]) =>
      gqlClient.request(updateSubjectGroups, { input }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: groupsKeys.subject.all() });
    },
  });
}

export function useSwitchSubjectGroupType() {
  return useMutation({
    mutationFn: (input: Core_SwitchSubjectGroupType) =>
      gqlClient.request(switchSubjectGroupType, { input }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: groupsKeys.subject.all() });
      queryClient.invalidateQueries({ queryKey: groupsKeys.support.all() });
    },
  });
}

export type ReturnTypeOfUseSubjectGroupList = UseQueryReturnType<
  typeof useSubjectGroups
>;

export type ReturnTypeOfUseSubjectGroupById = UseQueryReturnType<
  typeof useSubjectGroupById
>;
